import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  GTranslate as GTranslateIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { isEnglishQuestionLocale } from '../utils/locale';
import { colors } from '../../../shared/styles/theme';

type Props = {
  questionLocale?: string | null;
  originalNode: React.ReactNode;
  englishNode?: React.ReactNode | null;
  accent?: 'theme' | 'legacy';
  sx?: SxProps<Theme>;
};

/**
 * Non-English with stored English: default shows English; Language icon toggles to original; GTranslate returns to English.
 */
export const QuestionEnglishInline: React.FC<Props> = ({
  questionLocale,
  originalNode,
  englishNode,
  accent = 'theme',
  sx,
}) => {
  const [showOriginal, setShowOriginal] = useState(false);

  const iconColor = accent === 'legacy' ? '#48a1ad' : colors.primaryLight;

  if (!englishNode || !questionLocale || isEnglishQuestionLocale(questionLocale)) {
    return <Box sx={sx}>{originalNode}</Box>;
  }

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{showOriginal ? originalNode : englishNode}</Box>
        <Tooltip title={showOriginal ? 'Show English' : 'Show original language'}>
          <span>
            <IconButton
              size="small"
              aria-pressed={showOriginal}
              aria-label={showOriginal ? 'Show English translation' : 'Show original language question'}
              onClick={() => setShowOriginal(!showOriginal)}
              sx={{
                color: iconColor,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(72, 161, 173, 0.12)' },
              }}
            >
              {showOriginal ? (
                <GTranslateIcon fontSize="small" />
              ) : (
                <LanguageIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
